import { createId as cuid2 } from "@paralleldrive/cuid2";

import {
  defaultCertification,
  defaultEducation,
  defaultExperience,
  defaultInternship,
  defaultProject,
  defaultResumeData,
  defaultSkill,
  ResumeData,
  URL as ResumeURL,
} from "../../../../../../libs/schema/src";

const knownSectionNames = [
  "summary",
  "education",
  "experience",
  "projects",
  "skills",
  "certifications",
  "certificate / certifications",
  "certificates",
  "publications",
  "volunteer",
  "interests",
  "languages",
  "internship",
];

const cloneResumeData = (resumeData: ResumeData) =>
  JSON.parse(JSON.stringify(resumeData)) as ResumeData;

/**
 * ⚠️ THIS USED TO BE `Math.random().toString(36).slice(2, 10)`, A LOCAL SHADOW, AND IT MADE EVERY
 * RÉSUMÉ BUILT FROM AN UPLOAD UNSAVABLE.
 *
 * Every item id in the schema is `idSchema` — `z.string().cuid2()` — and zod 3.22's `cuid2Regex` is
 * `/^[a-z][a-z0-9]*$/`. A base-36 fraction starts with a DIGIT roughly 28% of the time, so with a
 * dozen parsed items at least one invalid id was near-certain, and `PATCH /resume/:id` answered every
 * autosave with `400 Validation failed / Invalid cuid2` naming the offending item paths.
 *
 * Why it looked like "the AI's changes don't save": the 400 landed on the *whole document*, so a
 * single bad id generated at upload time blocked every subsequent write — accepted suggestions,
 * manual edits, everything — for the life of that résumé. And the charset is otherwise legal, so the
 * ids that happened to start with a letter passed, which is what made it look intermittent.
 */
const createId = cuid2;

const getFileBaseName = (file: File) => file.name.replace(/\.[^.]+$/, "").trim();

const normalizeLines = (value: string) =>
  value
    .replace(/\r/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter((line) => line.length > 0);

const isSectionHeading = (line: string) => {
  const normalized = line.toLowerCase().replace(/[:]+$/, "").trim();
  return knownSectionNames.includes(normalized);
};

const isContactLine = (line: string) =>
  /@|\b(?:linkedin|portfolio|github|website|phone|mobile|dob|date of birth|age)\b/i.test(line) ||
  /\+?\d[\d\s().-]{6,}\d/.test(line);

const stripListPrefix = (line: string) =>
  line.replace(/^\s*(?:[-*•·?|]|\d+[\).\]])\s+/u, "").trim();

const normalizeContentLine = (line: string) => stripListPrefix(line);

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const extractDisplayName = (lines: string[]) =>
  lines.find((line) => !isContactLine(line) && !isSectionHeading(line)) ?? "";

const splitIntoSections = (text: string) => {
  const sections: Record<string, string[]> = {};
  const preamble: string[] = [];
  let currentSection = "summary";
  let hasSeenHeading = false;

  for (const rawLine of text.replace(/\r/g, "\n").split("\n")) {
    const line = rawLine.replace(/\s+/g, " ").trim();
    if (!line) continue;

    if (isSectionHeading(line)) {
      currentSection = line.toLowerCase().replace(/[:]+$/, "").trim();
      if (currentSection === "certificate / certifications") currentSection = "certifications";
      if (currentSection === "certificates") currentSection = "certifications";
      sections[currentSection] ??= [];
      hasSeenHeading = true;
      continue;
    }

    if (!hasSeenHeading) {
      preamble.push(line);
      continue;
    }

    sections[currentSection] ??= [];
    sections[currentSection].push(line);
  }

  return { preamble, sections };
};

const takeFirstLine = (text: string) => normalizeLines(text)[0] ?? "";

const blockText = (lines?: string[]) => (lines ?? []).join(" ").trim();

const splitContactTokens = (line: string) =>
  line
    .split(/\s*\|\s*|\s{2,}/)
    .map((token) => token.trim())
    .filter(Boolean);

const normalizeUrlCandidate = (value: string) => {
  const trimmed = value.trim().replace(/[)\],.;]+$/g, "");
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  if (/^(?:www\.)?[a-z0-9.-]+\.[a-z]{2,}(?:\/\S*)?$/i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  return "";
};

const extractContactDetails = (lines: string[]) => {
  const defaultPortfolio: ResumeURL = defaultResumeData.basics.portfolio ?? defaultResumeData.basics.url;
  const contact = {
    email: "",
    phone: "",
    url: { ...defaultResumeData.basics.url },
    portfolio: { ...defaultPortfolio },
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const tokens = splitContactTokens(line);

    for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex += 1) {
      const token = tokens[tokenIndex];
      const lowerToken = token.toLowerCase();
      const nextToken = tokens[tokenIndex + 1] ?? "";
      const nextUrl = normalizeUrlCandidate(nextToken);

      if (!contact.email) {
        const emailMatch = token.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
        if (emailMatch) {
          contact.email = emailMatch[0];
          continue;
        }
      }

      if (!contact.phone) {
        const phoneMatch = token.match(/\+?\d[\d\s().-]{6,}\d/);
        if (phoneMatch) {
          contact.phone = phoneMatch[0].replace(/\s+/g, " ").trim();
          continue;
        }
      }

      if (lowerToken === "linkedin" || lowerToken === "portfolio") {
        if (nextUrl) {
          const key = lowerToken === "linkedin" ? "url" : "portfolio";
          contact[key] = {
            ...(key === "url" ? defaultResumeData.basics.url : defaultResumeData.basics.portfolio),
            label: token,
            href: nextUrl,
          };
          tokenIndex += 1;
        }
        continue;
      }

      const url = normalizeUrlCandidate(token);
      if (!url) continue;

      if (!contact.url.href && /linkedin/i.test(token)) {
        contact.url = {
          ...defaultResumeData.basics.url,
          label: "LinkedIn",
          href: url,
        };
        continue;
      }

      if (!contact.portfolio.href && /portfolio/i.test(token)) {
        contact.portfolio = {
          ...defaultResumeData.basics.portfolio,
          label: "Portfolio",
          href: url,
        };
      }
    }
  }

  return contact;
};

const cleanSummaryLines = (lines?: string[]) =>
  (lines ?? []).filter((line) => !isContactLine(line) && !isSectionHeading(line));

const isTimelineLine = (line: string) =>
  /^(?:19|20)\d{2}$/.test(line.trim()) ||
  /\b(?:19|20)\d{2}\b.*(?:-|–|—|to).*(?:\b(?:19|20)\d{2}\b|\bpresent\b|\bcurrent\b|\bnow\b)/i.test(line) ||
  /^(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+\d{4}\s*(?:-|–|—|to)\s*(?:\b(?:19|20)\d{2}\b|\bpresent\b|\bcurrent\b|\bnow\b)/i.test(
    line,
  );

const isLikelyLocationLine = (line: string) => {
  const cleaned = normalizeContentLine(line);
  if (!cleaned) return false;
  if (/^\s*[-*•·?]\s+/u.test(line)) return false;
  if (isContactLine(cleaned) || isTimelineLine(cleaned) || isSectionHeading(cleaned)) return false;
  if (cleaned.length > 40) return false;
  if (
    /\b(?:engineer|developer|manager|intern|consultant|analyst|designer|scientist|architect|assistant|associate|director|professor|student|lead|specialist|officer)\b/i.test(
      cleaned,
    )
  ) {
    return false;
  }

  const words = cleaned.split(/\s+/);
  return (
    (words.length <= 3 && /^[A-Z][A-Z\s,'().-]*$/.test(cleaned)) ||
    (words.length <= 2 && /^[A-Z][a-z]+(?:[\s,'().-][A-Za-z]+)*$/.test(cleaned))
  );
};

const extractTimelineLine = (lines: string[]) => lines.find((line) => isTimelineLine(line)) ?? "";

const buildStructuredSummary = (lines: string[]) =>
  lines
    .map(normalizeContentLine)
    .filter((line) => line.length > 0)
    .filter((line) => !isContactLine(line) && !isSectionHeading(line) && !isTimelineLine(line) && !isLikelyLocationLine(line))
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("")
    .trim();

const isLikelyItemHeading = (line: string) => {
  if (/^\s*[-*•·?|]\s+/u.test(line)) return false;
  const cleaned = normalizeContentLine(line);
  if (!cleaned) return false;
  if (isSectionHeading(cleaned) || isContactLine(cleaned) || isLikelyLocationLine(cleaned)) return false;
  if (cleaned.length > 100) return false;

  const upperCaseSignal = cleaned === cleaned.toUpperCase() && !/\d/.test(cleaned);
  const titleCaseSignal =
    /^[A-Z][A-Za-z&'().,\-\/\s|]+$/.test(cleaned) && !/[.!?]$/.test(cleaned);
  const allCapsWordCount = cleaned
    .split(/\s+/)
    .filter((word) => word.length > 1 && /^[A-Z][A-Z&'().,\-\/|]*$/.test(word)).length;

  return upperCaseSignal || titleCaseSignal || allCapsWordCount >= 2;
};

const splitSectionIntoBlocks = (lines: string[]) => {
  const blocks: string[][] = [];
  let current: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const nextLine = lines[index + 1] ?? "";
    const hasEnoughContentToSplit =
      current.length >= 3 || current.some((entry) => /^[-*•]/.test(entry) || /\b(?:19|20)\d{2}\b/.test(entry));

    if (
      isLikelyLocationLine(line) &&
      current.length > 0 &&
      (isTimelineLine(nextLine) || /\b(?:19|20)\d{2}\b/.test(nextLine) || nextLine === "")
    ) {
      current.push(line);
      continue;
    }

    if (isLikelyItemHeading(line) && current.length > 0 && hasEnoughContentToSplit) {
      blocks.push(current);
      current = [line];
      continue;
    }

    current.push(line);
  }

  if (current.length > 0) blocks.push(current);

  return blocks.filter((block) => block.some((line) => line.trim().length > 0));
};

const buildExperienceItems = (lines: string[]) =>
  splitSectionIntoBlocks(lines).map((block) => {
    const [company, role, ...rest] = block;
    const date = extractTimelineLine(block);
    const location = block.slice(2).find((line) => isLikelyLocationLine(line)) ?? "";
    const summary = buildStructuredSummary(rest) || buildStructuredSummary(block.slice(2));

    return {
      ...defaultExperience,
      id: createId(),
      company: { ...defaultExperience.company, label: company || "Experience" },
      position: role || company || "Experience",
      location,
      date,
      summary,
      url: { ...defaultExperience.url },
    };
  });

const buildInternshipItems = (lines: string[]) => {
  const normalizedLines = lines.map(normalizeContentLine).filter(Boolean);
  if (normalizedLines.length === 0) return [];

  const company = normalizedLines[0] || "Internship";
  const role = normalizedLines[1] || company;
  const date = extractTimelineLine(normalizedLines);
  const location = normalizedLines.find((line, index) => index > 1 && isLikelyLocationLine(line)) ?? "";
  const summary = buildStructuredSummary(normalizedLines.slice(2)) || buildStructuredSummary(normalizedLines);

  return [
    {
      ...defaultInternship,
      id: createId(),
      company: { ...defaultInternship.company, label: company },
      position: role,
      location,
      date,
      summary,
      url: { ...defaultInternship.url },
      companyDescription: "",
    },
  ];
};

const buildEducationItems = (lines: string[]) =>
  splitSectionIntoBlocks(lines).map((block) => {
    const [institution, detail, ...rest] = block;
    const date = extractTimelineLine(block);
    const location = block.slice(2).find((line) => isLikelyLocationLine(line)) ?? "";
    const summary = buildStructuredSummary(rest) || buildStructuredSummary(block.slice(2));

    return {
      ...defaultEducation,
      id: createId(),
      institution: {
        ...defaultEducation.institution,
        label: institution || "Education",
      },
      studyType: detail || institution || "Education",
      area: location,
      score: "",
      date,
      summary,
      url: { ...defaultEducation.url },
    };
  });

const buildProjectItems = (lines: string[]) =>
  splitSectionIntoBlocks(lines).map((block) => {
    const [title, detail, ...rest] = block;
    const summary = buildStructuredSummary(rest) || buildStructuredSummary(block.slice(2));

    return {
      ...defaultProject,
      id: createId(),
      name: title || "Projects",
      description: (detail || summary || title || "Projects").slice(0, 60),
      date: "",
      summary,
      url: { ...defaultProject.url },
    };
  });

const buildCertificationItems = (lines: string[]) =>
  splitSectionIntoBlocks(lines).map((block) => {
    const [name, issuer, ...rest] = block;
    const summary = buildStructuredSummary(rest) || buildStructuredSummary(block.slice(2));

    return {
      ...defaultCertification,
      id: createId(),
      name: name || "Certifications",
      issuer: issuer || "",
      date: "",
      summary,
      url: { ...defaultCertification.url },
    };
  });

const buildSkillItems = (lines: string[]) => {
  const tokens = lines
    .map(normalizeContentLine)
    .filter((line) => !isSectionHeading(line) && !isContactLine(line) && !isLikelyLocationLine(line))
    .flatMap((line) => line.split(/[|,;]/))
    .map((token) => token.replace(/^[A-Za-z &/().-]+:\s*/, "").trim())
    .filter(Boolean);

  if (tokens.length === 0) {
    return [
      {
        ...defaultSkill,
        id: createId(),
        name: lines
          .map(normalizeContentLine)
          .filter((line) => !isSectionHeading(line) && !isContactLine(line) && !isLikelyLocationLine(line))
          .join(" "),
        description: "",
        level: 1,
      },
    ];
  }

  return Array.from(new Set(tokens)).map((token) => ({
    ...defaultSkill,
    id: createId(),
    name: token,
    description: "",
    level: 1,
  }));
};

export const buildTemporaryUploadedResume = (file: File, extractedText: string): ResumeData => {
  const resumeData = cloneResumeData(defaultResumeData);
  const baseName = getFileBaseName(file) || "Uploaded Resume";
  const { preamble, sections } = splitIntoSections(extractedText);
  const nameFromPreamble = extractDisplayName(preamble);
  const summaryText = blockText(cleanSummaryLines(sections.summary)) || `${baseName} uploaded resume draft`;
  const contactDetails = extractContactDetails(preamble);

  resumeData.basics.name = nameFromPreamble || baseName;
  resumeData.basics.headline = takeFirstLine(summaryText);
  resumeData.basics.email = contactDetails.email;
  resumeData.basics.phone = contactDetails.phone;
  resumeData.basics.url = contactDetails.url.href ? contactDetails.url : defaultResumeData.basics.url;
  resumeData.basics.portfolio = contactDetails.portfolio.href
    ? contactDetails.portfolio
    : defaultResumeData.basics.portfolio;
  resumeData.sections.summary.content = summaryText;

  const educationItems = buildEducationItems(sections.education ?? []);
  const experienceItems = buildExperienceItems(sections.experience ?? []);
  const internshipItems = buildInternshipItems(sections.internship ?? []);
  const projectItems = buildProjectItems(sections.projects ?? []);
  const certificationItems = buildCertificationItems(sections.certifications ?? []);
  const skillItems = buildSkillItems(sections.skills ?? []);

  if (educationItems.length > 0) resumeData.sections.education.items = educationItems;
  if (experienceItems.length > 0) resumeData.sections.experience.items = experienceItems;
  if (internshipItems.length > 0) resumeData.sections.internship.items = internshipItems;
  if (projectItems.length > 0) resumeData.sections.projects.items = projectItems;
  if (certificationItems.length > 0) resumeData.sections.certifications.items = certificationItems;
  if (skillItems.length > 0) resumeData.sections.skills.items = skillItems;

  return resumeData;
};
